import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-card text-foreground border-border',
        destructive:
          'border-destructive/30 text-foreground bg-destructive/5 dark:bg-destructive/10 dark:border-destructive/50 [&>svg]:text-destructive',
        success:
          'border-success/50 text-success bg-success/5 dark:border-success [&>svg]:text-success',
        // RUT-3732 — the one warning treatment: near-black body text, the hue
        // carried by the border and the icon, the fill a wash under 10%. The
        // wash is an alpha over the card, like every sibling variant here, so
        // `text-foreground` stays correct in both themes; the border and icon
        // use the fixed warning tokens. Needs no `dark:` overrides either way.
        warning:
          'border-warning-border text-foreground bg-warning/10 [&>svg]:text-warning',
        info:
          'border-primary/30 text-foreground bg-primary/5 dark:border-primary/50 dark:bg-primary/10 [&>svg]:text-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('text-xs font-medium leading-none', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-xs [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
